import {
    OperationVariables,
    QueryHookOptions,
    QueryResult
} from "@apollo/client";
import { Footer } from "components/core/templates/Footer";
import { MixedContentGrid } from "components/domain/contents/MixedContentGrid";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useHitBottom } from "hooks/useHitBottom";
import {
    ComponentProps,
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";
import { LIMIT } from "utils/buildQueryVariables";

import { ContentGrid } from "./ContentGrid";

type BaseContentGridProps = Omit<
    ComponentProps<typeof ContentGrid>,
    "contents" | "showSkeleton" | "loading"
>;

export const ContentGridQuery = <Q, V extends OperationVariables>({
    type = "default",
    variables,
    extractor,
    useQuery,
    updateQuery,
    limit = LIMIT.default,
    disablePauseAt32 = false,
    ...props
}: BaseContentGridProps & {
    type?: "default" | "mixed";
    variables: V;
    extractor: (result: Q) => Mix_ContentFragment[];
    useQuery: (options: QueryHookOptions<Q, V>) => QueryResult<Q, V>;
    updateQuery?: (
        previousQueryResult: any,
        options: {
            fetchMoreResult: any;
            variables: {
                offset: number;
            };
        }
    ) => any;
    limit?: number;
    disablePauseAt32?: boolean;
} & Record<string, unknown>) => {
    const { isFeatureEnabled } = useFeatureFlags();
    const { data, loading, fetchMore } = useQuery({
        fetchPolicy: "cache-and-network",
        notifyOnNetworkStatusChange: true,
        variables: {
            ...variables,
            limit: limit,
            offset: 0
        }
    });

    const contents = useMemo(
        () => (data ? extractor(data) : []),
        [data, extractor]
    );

    const [reachEnd, setReachEnd] = useState(false);
    const [hasClickedLoadMore, setHasClickedLoadMore] = useState(false);

    // Pause infinite scroll at 32 items until user clicks "Load More" (unless disabled or feature flag is off)
    const isPaused =
        !disablePauseAt32 &&
        contents.length >= 32 &&
        !hasClickedLoadMore &&
        isFeatureEnabled("UI_HEADER_DROPDOWN");

    const fetch = useCallback(() => {
        if (!loading && !reachEnd && updateQuery) {
            fetchMore({
                variables: {
                    ...variables,
                    offset: contents.length
                },
                updateQuery
            }).then((response) => {
                if (extractor(response.data).length === 0) {
                    setReachEnd(true);
                }
            });
        }
    }, [
        contents.length,
        loading,
        variables,
        fetchMore,
        reachEnd,
        updateQuery,
        extractor
    ]);

    useEffect(() => {
        setReachEnd(false);
    }, [variables]);

    useHitBottom({ fetch, enabled: !isPaused });

    const Component = {
        mixed: MixedContentGrid,
        default: ContentGrid
    }[type];

    const handleLoadMore = useCallback(() => {
        setHasClickedLoadMore(true);
    }, []);

    return (
        <>
            <Component
                contents={contents}
                showSkeleton={loading}
                loading={loading}
                {...props}
            />
            {isPaused &&
                !reachEnd &&
                isFeatureEnabled("UI_HEADER_DROPDOWN") && (
                    <div className="flex w-full items-center justify-center p-8">
                        <button
                            onClick={handleLoadMore}
                            className="shadow-[rgba(60, 64, 67, 0.3) 0px 1px 2px
                                0px, rgba(60, 64, 67, 0.15) 0px 2px 6px 2px]
                                cursor-pointer rounded-lg border border-black
                                bg-white px-8 py-3 text-base font-semibold
                                text-primary shadow-md transition-all
                                duration-200 ease-in-out hover:-translate-y-0.5
                                hover:shadow-lg dark:border-white
                                dark:bg-[#171717]"
                        >
                            더 보기
                        </button>
                    </div>
                )}
            {(reachEnd || isPaused) &&
                isFeatureEnabled("UI_HEADER_DROPDOWN") && (
                    <Footer className="hidden lg:block" />
                )}
        </>
    );
};
